/**
 * CR283: Results Meta Block Editor
 * 
 * Editor component for configuring the results count display.
 * Provides controls for template, font, size, and alignment.
 */

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { FontPicker } from "@/components/ui/font-picker";
import { Separator } from "@/components/ui/separator";
import type { ResultsMetaBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_RESULTS_META_CONFIG } from "@/lib/layout-studio/types";
import { AlignLeft, AlignCenter, AlignRight, Type } from "lucide-react";

interface ResultsMetaBlockEditorProps {
  config: ResultsMetaBlockConfig;
  onChange: (config: Partial<ResultsMetaBlockConfig>) => void;
}

export function ResultsMetaBlockEditor({ config, onChange }: ResultsMetaBlockEditorProps) {
  const mergedConfig = { ...DEFAULT_RESULTS_META_CONFIG, ...config };

  return (
    <div className="space-y-4">
      {/* Show Count Toggle */}
      <div className="flex items-center justify-between">
        <Label className="text-xs">Show Results Count</Label>
        <Switch
          checked={mergedConfig.showCount}
          onCheckedChange={(v) => onChange({ showCount: v })}
        />
      </div>

      {mergedConfig.showCount && (
        <>
          <Separator />
          
          {/* Template */}
          <div className="space-y-2">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <Type className="h-3 w-3" />
              Text Template
            </Label>
            <Input
              value={mergedConfig.template || "Showing {count} {dealWord}"}
              onChange={(e) => onChange({ template: e.target.value })}
              placeholder="Showing {count} {dealWord}"
              className="h-7 text-xs font-mono"
            />
            <p className="text-[10px] text-muted-foreground">
              Use <code className="bg-muted px-1 rounded">{"{count}"}</code> for number and <code className="bg-muted px-1 rounded">{"{dealWord}"}</code> for "deal/deals"
            </p>
          </div>

          <Separator />

          {/* Typography Controls */}
          <div className="space-y-3">
            <Label className="text-xs font-medium">Typography</Label>
            
            {/* Font Family */}
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Font Family</Label>
              <FontPicker
                value={mergedConfig.fontFamily || null}
                onChange={(fontId) => onChange({ fontFamily: fontId })}
                placeholder="Inherit from theme"
                size="sm"
                showPreview
              />
            </div>

            {/* Font Size */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] text-muted-foreground">Font Size</Label>
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {mergedConfig.fontSize || 14}px
                </span>
              </div>
              <Slider
                value={[mergedConfig.fontSize || 14]}
                onValueChange={([v]) => onChange({ fontSize: v })}
                min={10}
                max={32}
                step={1}
                className="w-full"
              />
            </div>

            {/* Alignment */}
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Alignment</Label>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => onChange({ align: "left" })}
                  className={`flex-1 flex items-center justify-center h-7 rounded border text-xs transition-colors ${
                    mergedConfig.align === "left" || !mergedConfig.align
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted border-input"
                  }`}
                >
                  <AlignLeft className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ align: "center" })}
                  className={`flex-1 flex items-center justify-center h-7 rounded border text-xs transition-colors ${
                    mergedConfig.align === "center"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted border-input"
                  }`}
                >
                  <AlignCenter className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange({ align: "right" })}
                  className={`flex-1 flex items-center justify-center h-7 rounded border text-xs transition-colors ${
                    mergedConfig.align === "right"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted border-input"
                  }`}
                >
                  <AlignRight className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
